<script setup>
import { computed, nextTick, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ChannelSidebar from '../components/ChannelSidebar.vue';
import ChannelHeader from '../components/ChannelHeader.vue';
import ChannelPanel from '../components/ChannelPanel.vue';
import ChannelFormDialog from '../components/ChannelFormDialog.vue';
import MessageList from '../components/MessageList.vue';
import MessageComposer from '../components/MessageComposer.vue';
import TypingIndicator from '../components/TypingIndicator.vue';
import MessageInfoDialog from '../components/MessageInfoDialog.vue';
import { channelDisplayName } from '../helpers/timeline';

// Page size of the messages endpoint: a full page means there may be more.
const MESSAGES_PER_PAGE = 30;
// Wide screens open the first channel right away instead of an empty screen.
const DESKTOP_QUERY = '(min-width: 768px)';
const DEFAULT_CHANNEL_SLUG = 'geral';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const { can } = usePermissions();

const channels = useMapGetter('teamChat/getChannels');
const uiFlags = useMapGetter('teamChat/getUIFlags');
const currentUserId = useMapGetter('getCurrentUserID');
const currentRole = useMapGetter('getCurrentRole');
const agents = useMapGetter('agents/getVerifiedAgents');

const formRef = ref(null);
const deleteRef = ref(null);
const panel = ref(null);
const panelRefreshKey = ref(0);
const pageByChannel = ref({});
const hasMoreByChannel = ref({});
const isLoadingOlder = ref(false);
const composerRef = ref(null);
const listRef = ref(null);
const infoRef = ref(null);
// Search result waiting for its channel to load: { channelId, messageId }.
const pendingJump = ref(null);
// Older pages loaded at most while looking for a search result.
const MAX_PAGES_TO_FIND = 15;
const PANEL_SIDE_QUERY = '(min-width: 1024px)';
// Message being answered in the active channel (WhatsApp-style).
const replyTo = ref(null);

const canManage = computed(() => can('team_chat.manage'));
const isAccountAdmin = computed(() => currentRole.value === 'administrator');

const activeChannelId = computed(() =>
  route.params.channelId ? Number(route.params.channelId) : null
);
const receipts = computed(() =>
  activeChannelId.value
    ? store.getters['teamChat/getReceipts'](activeChannelId.value)
    : {}
);
const activeChannel = computed(() =>
  channels.value.find(channel => channel.id === activeChannelId.value)
);
const messages = computed(() =>
  activeChannelId.value
    ? store.getters['teamChat/getMessages'](activeChannelId.value)
    : []
);
const members = computed(() =>
  activeChannelId.value
    ? store.getters['teamChat/getMembers'](activeChannelId.value)
    : []
);

const agentsById = computed(
  () => new Map(agents.value.map(agent => [agent.id, agent]))
);
const statusFor = userId => agentsById.value.get(userId)?.availability_status;
const nameFor = userId => {
  const agent = agentsById.value.get(userId);
  return agent ? agent.available_name || agent.name : '';
};
const onlineAgents = computed(() =>
  agents.value.filter(
    agent =>
      agent.id !== currentUserId.value &&
      ['online', 'busy'].includes(agent.availability_status)
  )
);

// Public channels belong to the whole team; private ones to their members.
const people = computed(() => {
  if (activeChannel.value?.private) {
    return members.value.map(member => ({
      id: member.id,
      name: member.user?.available_name || member.user?.name,
      thumbnail: member.user?.thumbnail,
      role: member.role,
    }));
  }
  return agents.value.map(agent => ({
    id: agent.id,
    name: agent.available_name || agent.name,
    thumbnail: agent.thumbnail,
    role: null,
  }));
});
const typingUsers = computed(() =>
  activeChannelId.value
    ? store.getters['teamChat/getTyping'](activeChannelId.value).filter(
        user => user.id !== currentUserId.value
      )
    : []
);
const onTyping = ({ channelId, typing }) =>
  store.dispatch('teamChat/sendTyping', { channelId, typing });

const candidates = computed(() => {
  const memberIds = new Set(members.value.map(member => member.id));
  return agents.value
    .filter(agent => !memberIds.has(agent.id))
    .map(agent => ({
      id: agent.id,
      name: agent.available_name || agent.name,
      thumbnail: agent.thumbnail,
    }));
});
const inviteableAgents = computed(() =>
  agents.value.filter(agent => agent.id !== currentUserId.value)
);

const isChannelAdmin = computed(() =>
  members.value.some(
    member => member.id === currentUserId.value && member.role === 'admin'
  )
);
const canManageMembers = computed(
  () => isAccountAdmin.value || isChannelAdmin.value
);
const canManageChannel = computed(() => {
  if (!activeChannel.value) return false;
  if (isAccountAdmin.value || isChannelAdmin.value) return true;
  return !activeChannel.value.private && canManage.value;
});
const canPost = computed(
  () => !activeChannel.value?.read_only || canManageChannel.value
);

const openChannel = channel =>
  router.push(
    accountScopedRoute('team_chat_channel', { channelId: channel.id })
  );
const backToChannels = () => router.push(accountScopedRoute('team_chat_index'));

const openPanel = tab => {
  panel.value = panel.value === tab ? null : tab;
};

// Open a search result: its channel, older pages until it shows up, then
// scroll to it and flash it.
const jumpToPending = async () => {
  const pending = pendingJump.value;
  if (!pending || pending.channelId !== activeChannelId.value) return;
  pendingJump.value = null;
  for (let attempt = 0; attempt <= MAX_PAGES_TO_FIND; attempt += 1) {
    await nextTick();
    if (listRef.value?.jumpTo(pending.messageId, { quiet: true })) return;
    if (!hasMoreByChannel.value[pending.channelId]) break;
    listRef.value?.prepareOlder();
    // eslint-disable-next-line no-await-in-loop
    await onLoadOlder();
  }
  useAlert(t('TEAM_CHAT.SEARCH_NOT_FOUND'));
};

const onOpenResult = message => {
  pendingJump.value = {
    channelId: message.team_channel_id,
    messageId: message.id,
  };
  // On narrow screens the panel covers the conversation.
  if (!window.matchMedia(PANEL_SIDE_QUERY).matches) panel.value = null;
  if (message.team_channel_id === activeChannelId.value) {
    jumpToPending();
  } else {
    openChannel({ id: message.team_channel_id });
  }
};

const onReply = message => {
  replyTo.value = message;
  composerRef.value?.focus();
};

const onSend = async ({ content, files, replyToId }, { restore }) => {
  try {
    await store.dispatch('teamChat/sendMessage', {
      channelId: activeChannelId.value,
      content,
      files,
      replyToId,
    });
    replyTo.value = null;
  } catch {
    restore();
    useAlert(t('TEAM_CHAT.SEND_ERROR'));
  }
};

const onLoadOlder = async () => {
  const channelId = activeChannelId.value;
  const page = (pageByChannel.value[channelId] || 1) + 1;
  isLoadingOlder.value = true;
  try {
    const count = await store.dispatch('teamChat/fetchOlderMessages', {
      channelId,
      page,
    });
    pageByChannel.value[channelId] = page;
    hasMoreByChannel.value[channelId] = count >= MESSAGES_PER_PAGE;
  } finally {
    isLoadingOlder.value = false;
  }
};

const onToggleStar = async message => {
  try {
    await store.dispatch('teamChat/starMessage', {
      channelId: activeChannelId.value,
      messageId: message.id,
      starred: !message.starred,
    });
    panelRefreshKey.value += 1;
  } catch {
    useAlert(t('TEAM_CHAT.STAR_ERROR'));
  }
};

const onTogglePin = async message => {
  try {
    await store.dispatch('teamChat/pinMessage', {
      channelId: activeChannelId.value,
      messageId: message.id,
      pinned: !message.pinned,
    });
    panelRefreshKey.value += 1;
  } catch {
    useAlert(t('TEAM_CHAT.PIN_ERROR'));
  }
};

const onReact = async (message, emoji) => {
  try {
    await store.dispatch('teamChat/toggleReaction', {
      channelId: activeChannelId.value,
      messageId: message.id,
      emoji,
    });
  } catch {
    useAlert(t('TEAM_CHAT.REACT_ERROR'));
  }
};

const onToggleMute = async () => {
  try {
    await store.dispatch('teamChat/muteChannel', {
      channelId: activeChannelId.value,
      muted: !activeChannel.value.muted,
    });
  } catch {
    useAlert(t('TEAM_CHAT.MUTE_ERROR'));
  }
};

const isSavingChannel = ref(false);
const onSubmitChannel = async form => {
  isSavingChannel.value = true;
  try {
    if (form.channel) {
      await store.dispatch('teamChat/editChannel', {
        channelId: form.channel.id,
        name: form.name,
        description: form.description,
        readOnly: form.readOnly,
        announcement: form.announcement,
      });
    } else {
      const channel = await store.dispatch('teamChat/createChannel', {
        name: form.name,
        description: form.description,
        isPrivate: form.isPrivate,
        memberIds: form.memberIds,
      });
      openChannel(channel);
    }
    formRef.value?.close();
  } catch {
    useAlert(
      form.channel ? t('TEAM_CHAT.UPDATE_ERROR') : t('TEAM_CHAT.CREATE_ERROR')
    );
  } finally {
    isSavingChannel.value = false;
  }
};

const onConfirmDelete = async () => {
  try {
    await store.dispatch('teamChat/deleteChannel', activeChannelId.value);
    deleteRef.value?.close();
    backToChannels();
  } catch {
    useAlert(t('TEAM_CHAT.DELETE_ERROR'));
  }
};

const onAddMembers = async userIds => {
  try {
    await store.dispatch('teamChat/addMembers', {
      channelId: activeChannelId.value,
      userIds,
    });
  } catch {
    useAlert(t('TEAM_CHAT.MEMBERS_ERROR'));
  }
};

const onRemoveMember = async userId => {
  try {
    await store.dispatch('teamChat/removeMembers', {
      channelId: activeChannelId.value,
      userIds: [userId],
    });
  } catch {
    useAlert(t('TEAM_CHAT.MEMBERS_ERROR'));
  }
};

const onToggleRole = async person => {
  try {
    await store.dispatch('teamChat/updateMemberRole', {
      channelId: activeChannelId.value,
      userId: person.id,
      role: person.role === 'admin' ? 'member' : 'admin',
    });
  } catch {
    useAlert(t('TEAM_CHAT.MEMBERS_ERROR'));
  }
};

watch(
  activeChannelId,
  async id => {
    store.dispatch('teamChat/setActiveChannel', id);
    replyTo.value = null;
    if (!id) return;
    if (panel.value === 'pinned' || panel.value === 'starred') {
      panel.value = null;
    }
    await store.dispatch('teamChat/fetchMessages', id);
    pageByChannel.value[id] = 1;
    hasMoreByChannel.value[id] =
      store.getters['teamChat/getMessages'](id).length >= MESSAGES_PER_PAGE;
    store.dispatch('teamChat/markRead', id);
    store.dispatch('teamChat/fetchReceipts', id);
    jumpToPending();
  },
  { immediate: true }
);

// Private channels list their members; read-only ones need them to know who
// may post.
watch(activeChannel, channel => {
  if (channel && (channel.private || channel.read_only)) {
    store.dispatch('teamChat/fetchMembers', channel.id);
  }
});

watch(
  channels,
  list => {
    if (activeChannelId.value || !list.length) return;
    if (!window.matchMedia(DESKTOP_QUERY).matches) return;
    const first =
      list.find(channel => channel.slug === DEFAULT_CHANNEL_SLUG) ||
      [...list].sort((a, b) => a.name.localeCompare(b.name))[0];
    router.replace(
      accountScopedRoute('team_chat_channel', { channelId: first.id })
    );
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('teamChat/fetchChannels');
  // The @mention, member pickers and presence read these stores directly.
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});

onUnmounted(() => {
  store.dispatch('teamChat/setActiveChannel', null);
});
</script>

<template>
  <div class="relative flex w-full h-full min-h-0 bg-n-solid-1">
    <ChannelSidebar
      :class="activeChannel ? 'hidden md:flex' : 'flex'"
      :channels="channels"
      :active-channel-id="activeChannelId"
      :can-create="canManage"
      :online-agents="onlineAgents"
      @select="openChannel"
      @create="formRef?.open()"
    />

    <section
      v-if="activeChannel"
      class="flex flex-col flex-1 min-w-0 h-full bg-n-solid-1"
    >
      <ChannelHeader
        :channel="activeChannel"
        :people="people"
        :panel="panel"
        :can-manage="canManageChannel"
        :status-for="statusFor"
        @open-panel="openPanel"
        @toggle-mute="onToggleMute"
        @edit="formRef?.open(activeChannel)"
        @delete="deleteRef?.open()"
        @back="backToChannels"
      />
      <div
        v-if="activeChannel.announcement"
        class="flex items-start flex-shrink-0 gap-2 px-5 py-2 text-[0.8125rem] border-b text-n-amber-12 border-n-amber-5 bg-n-amber-2"
      >
        <span
          class="mt-0.5 flex-shrink-0 size-4 i-lucide-megaphone text-n-amber-10"
        />
        <span class="whitespace-pre-wrap">{{
          activeChannel.announcement
        }}</span>
      </div>
      <MessageList
        ref="listRef"
        :messages="messages"
        :channel="activeChannel"
        :can-pin="canManageChannel"
        :can-reply="canPost"
        :status-for="statusFor"
        :current-user-id="currentUserId"
        :name-for="nameFor"
        :receipts="receipts"
        :has-more="!!hasMoreByChannel[activeChannel.id]"
        :is-loading-older="isLoadingOlder"
        @star="onToggleStar"
        @pin="onTogglePin"
        @react="onReact"
        @reply="onReply"
        @load-older="onLoadOlder"
        @info="infoRef?.open($event)"
      />
      <TypingIndicator :users="typingUsers" />
      <MessageComposer
        v-if="canPost"
        ref="composerRef"
        :reply-to="replyTo"
        @cancel-reply="replyTo = null"
        :channel-id="activeChannel.id"
        :placeholder="
          t('TEAM_CHAT.MESSAGE_PLACEHOLDER', {
            name: channelDisplayName(activeChannel),
          })
        "
        :is-sending="uiFlags.isSending"
        @send="onSend"
        @typing="onTyping"
      />
      <p
        v-else
        class="px-5 py-4 text-sm text-center border-t text-n-slate-11 border-n-weak"
      >
        {{ t('TEAM_CHAT.READ_ONLY_NOTICE') }}
      </p>
    </section>

    <div
      v-else
      class="items-center justify-center flex-1 hidden text-center md:flex"
    >
      <div>
        <span
          class="inline-flex items-center justify-center mb-3 rounded-2xl size-14 bg-n-alpha-2 text-n-slate-10"
        >
          <span class="size-6 i-lucide-messages-square" />
        </span>
        <p class="text-sm text-n-slate-11">
          {{ t('TEAM_CHAT.SELECT_CHANNEL') }}
        </p>
      </div>
    </div>

    <ChannelPanel
      v-if="activeChannel && panel"
      class="absolute inset-0 z-30 lg:static"
      :channel="activeChannel"
      :tab="panel"
      :people="people"
      :candidates="candidates"
      :can-manage-members="canManageMembers"
      :can-manage-channel="canManageChannel"
      :current-user-id="currentUserId"
      :status-for="statusFor"
      :name-for="nameFor"
      :refresh-key="panelRefreshKey"
      @close="panel = null"
      @change-tab="panel = $event"
      @edit="formRef?.open(activeChannel)"
      @add-members="onAddMembers"
      @remove-member="onRemoveMember"
      @toggle-role="onToggleRole"
      @open-result="onOpenResult"
    />

    <MessageInfoDialog ref="infoRef" :receipts="receipts" />
    <ChannelFormDialog
      ref="formRef"
      :agents="inviteableAgents"
      :is-saving="isSavingChannel"
      @submit="onSubmitChannel"
    />
    <Dialog
      ref="deleteRef"
      type="alert"
      :title="t('TEAM_CHAT.DELETE')"
      :description="t('TEAM_CHAT.DELETE_CONFIRM')"
      :confirm-button-label="t('TEAM_CHAT.DELETE')"
      :cancel-button-label="t('TEAM_CHAT.CANCEL')"
      @confirm="onConfirmDelete"
    />
  </div>
</template>
